import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import { CommonTableColumn, CommonTableProps } from '../types/common.types';

type SortValue = string | number | boolean | Date | null | undefined;

interface GroupNode<T> {
  label: string;
  // Unique across the whole tree (prefixed by every ancestor's label) — used
  // as the collapse-state key, so two different branches that happen to share
  // a sub-group label (e.g. "Open" under two different clients) collapse independently.
  path: string;
  rows: T[];
  // null at the deepest grouped level — that's where actual data rows render.
  children: GroupNode<T>[] | null;
}

// Undefined/null/'' always sort last regardless of direction — a "missing value"
// isn't meaningfully greater or smaller than a real one in either direction.
const compareSortValues = (a: SortValue, b: SortValue): number => {
  const aEmpty = a === null || a === undefined || a === '';
  const bEmpty = b === null || b === undefined || b === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  if (a instanceof Date || b instanceof Date) {
    const aTime = a instanceof Date ? a.getTime() : new Date(String(a)).getTime();
    const bTime = b instanceof Date ? b.getTime() : new Date(String(b)).getTime();
    if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) {
      return aTime - bTime;
    }
  }

  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }

  if (typeof a === 'boolean' && typeof b === 'boolean') {
    return Number(a) - Number(b);
  }

  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

const isColumnSortable = <T extends object>(column: CommonTableColumn<T>): boolean =>
  column.sortable ?? !column.render;

// Recursively partitions `sourceRows` by each key in turn — the first key is the
// outermost group, each key after it sub-groups within the level before. `path` is
// unique per node (prefixed by every ancestor), used as the collapse-state key so two
// branches that happen to share a sub-group label collapse independently.
const buildGroupNodes = <T extends object>(
  sourceRows: T[],
  keys: (keyof T)[],
  columns: CommonTableColumn<T>[],
  parentPath: string,
): GroupNode<T>[] => {
  const [currentKey, ...restKeys] = keys;
  const groupColumn = columns.find((column) => column.key === currentKey);
  const accessor =
    groupColumn?.groupAccessor ?? groupColumn?.sortAccessor ?? ((row: T) => row[currentKey] as unknown as SortValue);

  const order: string[] = [];
  const map = new Map<string, T[]>();
  sourceRows.forEach((row) => {
    const raw = accessor(row);
    const label = raw === null || raw === undefined || raw === '' ? 'Unspecified' : String(raw);
    const bucket = map.get(label);
    if (bucket) {
      bucket.push(row);
    } else {
      map.set(label, [row]);
      order.push(label);
    }
  });

  return order
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
    .map((label) => {
      const groupRows = map.get(label) as T[];
      const path = `${parentPath}/${String(currentKey)}:${label}`;
      return {
        label,
        path,
        rows: groupRows,
        children: restKeys.length > 0 ? buildGroupNodes(groupRows, restKeys, columns, path) : null,
      };
    });
};

const CommonTable = <T extends object>({
  columns,
  rows,
  getRowId,
  emptyMessage = 'No records found',
  size = 'small',
  stickyHeader = false,
  enablePagination = true,
  defaultRowsPerPage = 10,
  rowsPerPageOptions = [10, 20, 30, 40, 50],
  serverSide = false,
  serverTotalCount,
  serverPage,
  serverRowsPerPage,
  onServerPageChange,
  onServerRowsPerPageChange,
  defaultSortKey,
  defaultSortDirection,
  onSortChange,
  sortKey: controlledSortKey,
  sortDirection: controlledSortDirection,
  groupByKeys,
  paginationVariant = 'both',
}: CommonTableProps<T>): React.ReactElement => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [internalSortState, setInternalSortState] = useState<{ key: keyof T; direction: 'asc' | 'desc' } | null>(() =>
    defaultSortKey === undefined ? null : { key: defaultSortKey, direction: defaultSortDirection ?? 'asc' },
  );
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // Controlled when the caller passes `sortKey` (even `null`, meaning "no sort, but I'm
  // driving it") — otherwise the table manages its own sort state from header clicks alone.
  // Keyed by field rather than column index so a field that isn't rendered as a visible
  // column (passed only via GroupSortBar) can still be sorted on.
  const isSortControlled = controlledSortKey !== undefined;
  const sortState = useMemo<{ key: keyof T; direction: 'asc' | 'desc' } | null>(() => {
    if (!isSortControlled) return internalSortState;
    if (!controlledSortKey) return null;
    return { key: controlledSortKey, direction: controlledSortDirection ?? 'asc' };
  }, [isSortControlled, controlledSortKey, controlledSortDirection, internalSortState]);

  useEffect(() => {
    setRowsPerPage(defaultRowsPerPage);
  }, [defaultRowsPerPage]);

  useEffect(() => {
    if (serverSide) return;
    const totalPages = Math.max(1, Math.ceil(rows.length / rowsPerPage));
    if (page > totalPages - 1) {
      setPage(totalPages - 1);
    }
  }, [page, rows.length, rowsPerPage, serverSide]);

  const sortedRows = useMemo(() => {
    if (!sortState) {
      return rows;
    }

    const column = columns.find((candidate) => candidate.key === sortState.key);
    const accessor = column?.sortAccessor ?? ((row: T) => row[sortState.key] as unknown as SortValue);
    const multiplier = sortState.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => multiplier * compareSortValues(accessor(a), accessor(b)));
  }, [rows, sortState, columns]);

  // Grouping is applied on top of the already-sorted rows. On a non-serverSide table this
  // takes over from row-level pagination entirely (every sorted row renders under its
  // section) since slicing to a page would otherwise split a group across pages. Keys
  // don't need to match a visible column — falls back to the raw field value when they
  // don't. Multiple keys nest: the first is the outermost group, each one after it
  // sub-groups within the level before.
  const activeGroupKeys = useMemo(
    () => (groupByKeys ?? []).filter((key): key is keyof T => key !== null && key !== undefined),
    [groupByKeys],
  );

  const groupTree = useMemo(() => {
    if (activeGroupKeys.length === 0) {
      return null;
    }
    return buildGroupNodes(sortedRows, activeGroupKeys, columns, '');
  }, [activeGroupKeys, columns, sortedRows]);

  const isGrouped = groupTree !== null;
  const showPaginationFooter = enablePagination && !(isGrouped && !serverSide);

  const visibleRows = useMemo(() => {
    if (isGrouped || !enablePagination || serverSide) {
      return sortedRows;
    }

    const start = page * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [isGrouped, enablePagination, page, sortedRows, rowsPerPage, serverSide]);

  const activePage = serverSide ? (serverPage ?? 0) : page;
  const activeRowsPerPage = serverSide ? (serverRowsPerPage ?? defaultRowsPerPage) : rowsPerPage;
  const activeCount = serverSide ? (serverTotalCount ?? 0) : rows.length;

  const toggleGroupCollapsed = (path: string): void => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const handleSortClick = (column: CommonTableColumn<T>): void => {
    if (!isColumnSortable(column)) {
      return;
    }

    let next: { key: keyof T; direction: 'asc' | 'desc' } | null;
    if (!sortState || sortState.key !== column.key) {
      next = { key: column.key, direction: 'asc' };
    } else if (sortState.direction === 'asc') {
      next = { key: column.key, direction: 'desc' };
    } else {
      next = null;
    }

    if (!isSortControlled) {
      setInternalSortState(next);
    }
    onSortChange?.(next ? next.key : null, next?.direction ?? 'asc');
  };

  const renderDataRow = (row: T, rowKeyIndex: number | string): React.ReactElement => (
    <TableRow key={getRowId ? getRowId(row, Number(rowKeyIndex) || 0) : String(rowKeyIndex)} hover>
      {columns.map((column, columnIndex) => (
        <TableCell key={`${String(column.key)}-${columnIndex}`} align={column.align ?? 'left'}>
          {column.render ? column.render(row) : String(row[column.key] ?? '')}
        </TableCell>
      ))}
    </TableRow>
  );

  // Renders one group level and recurses into its children (if any) — depth only
  // controls indentation; collapse state is tracked per node via its full `path`.
  const renderGroupNode = (node: GroupNode<T>, depth: number, keyPrefix: string): React.ReactElement => {
    const collapsed = collapsedGroups.has(node.path);
    return (
      <React.Fragment key={node.path}>
        <TableRow hover onClick={() => toggleGroupCollapsed(node.path)} sx={{ cursor: 'pointer', backgroundColor: depth === 0 ? '#eef6f8' : '#f5fafb' }}>
          <TableCell colSpan={columns.length} sx={{ py: 0.75, pl: 2 + depth * 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {collapsed ? (
                <KeyboardArrowRightRoundedIcon fontSize="small" sx={{ color: '#2F7E8E' }} />
              ) : (
                <KeyboardArrowDownRoundedIcon fontSize="small" sx={{ color: '#2F7E8E' }} />
              )}
              <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{node.label}</Typography>
              <Typography variant="caption" color="text.secondary">
                ({node.rows.length})
              </Typography>
            </Box>
          </TableCell>
        </TableRow>
        {collapsed
          ? null
          : node.children
            ? node.children.map((child, childIndex) => renderGroupNode(child, depth + 1, `${keyPrefix}-${childIndex}`))
            : node.rows.map((row, rowIndex) => renderDataRow(row, `${keyPrefix}-${rowIndex}`))}
      </React.Fragment>
    );
  };

  return (
    <Paper
      sx={{
        width: '100%',
        maxWidth: '100%',
        // Percentage height only takes effect inside a height-constrained
        // ancestor (e.g. a modal's flex scroll area) — a no-op otherwise, so
        // this is safe for both constrained and natural-height usages.
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        // Lets a flex/height-constrained ancestor (e.g. a modal's scroll area)
        // shrink this table's row body instead of pushing the pagination
        // footer below — without this the footer can end up clipped or
        // require scrolling the whole table to reach, at some widths/heights.
        minHeight: 0,
      }}
    >
      <TableContainer
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowX: 'auto',
          overflowY: 'auto',
          scrollbarWidth: 'thin',
          scrollbarColor: '#2F7E8E #e6f1f4',
          '&::-webkit-scrollbar': {
            width: 6,
            height: 6,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#e6f1f4',
            borderRadius: 8,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#2F7E8E',
            borderRadius: 8,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#3f8593',
          },
        }}
      >
        <Table size={size} stickyHeader={stickyHeader} sx={{ minWidth: 'max-content' }}>
          <TableHead>
            <TableRow>
              {columns.map((column, columnIndex) => {
                const sortable = isColumnSortable(column);
                const isActive = sortState?.key === column.key;
                const direction = isActive && sortState ? sortState.direction : 'asc';
                return (
                  <TableCell
                    key={`${String(column.key)}-${columnIndex}`}
                    align={column.align ?? 'left'}
                    sortDirection={isActive ? direction : false}
                  >
                    {sortable ? (
                      <TableSortLabel active={isActive} direction={direction} onClick={() => handleSortClick(column)}>
                        {column.header}
                      </TableSortLabel>
                    ) : (
                      column.header
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {groupTree ? (
              groupTree.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    <Typography align="center">{emptyMessage}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                groupTree.map((node, groupIndex) => renderGroupNode(node, 0, String(groupIndex)))
              )
            ) : visibleRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Typography align="center">{emptyMessage}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              visibleRows.map((row, index) => renderDataRow(row, page * rowsPerPage + index))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {showPaginationFooter ? (
        <Box
          sx={{
            flex: '0 0 auto',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: paginationVariant === 'arrows' ? 'flex-end' : 'space-between',
          }}
        >
          {paginationVariant !== 'arrows' ? (
            <Pagination
              page={activePage + 1}
              count={Math.max(1, Math.ceil(activeCount / Math.max(activeRowsPerPage, 1)))}
              onChange={(_, nextPage) => {
                if (serverSide) {
                  onServerPageChange?.(nextPage - 1);
                } else {
                  setPage(nextPage - 1);
                }
              }}
              size="small"
              shape="rounded"
              showFirstButton
              showLastButton
              sx={{
                px: 2,
                py: 1,
                '& .MuiPaginationItem-root': {
                  minWidth: 34,
                  height: 34,
                  borderRadius: '10px',
                  border: '1px solid #d7e4e7',
                  backgroundColor: '#ffffff',
                  color: '#2f5a5f',
                  fontWeight: 600,
                },
                '& .MuiPaginationItem-root:hover': {
                  backgroundColor: '#eef6f8',
                },
                '& .MuiPaginationItem-root.Mui-selected': {
                  backgroundColor: '#2F7E8E',
                  borderColor: '#2F7E8E',
                  color: '#ffffff',
                  fontWeight: 700,
                },
                '& .MuiPaginationItem-root.Mui-selected:hover': {
                  backgroundColor: '#3f8593',
                },
                '& .MuiPaginationItem-ellipsis': {
                  border: 'none',
                  backgroundColor: 'transparent',
                },
              }}
            />
          ) : null}
          {paginationVariant !== 'numbered' ? (
            <TablePagination
              component="div"
              count={activeCount}
              page={activePage}
              sx={{ flex: paginationVariant === 'arrows' ? '1 1 auto' : '0 0 auto' }}
              onPageChange={(_, nextPage) => {
                if (serverSide) {
                  onServerPageChange?.(nextPage);
                } else {
                  setPage(nextPage);
                }
              }}
              rowsPerPage={activeRowsPerPage}
              onRowsPerPageChange={(event) => {
                const newRpp = Number(event.target.value);
                if (serverSide) {
                  onServerRowsPerPageChange?.(newRpp);
                } else {
                  setRowsPerPage(newRpp);
                  setPage(0);
                }
              }}
              rowsPerPageOptions={rowsPerPageOptions}
            />
          ) : null}
        </Box>
      ) : null}
    </Paper>
  );
};

export default CommonTable;
